import { construct, crush, isObject, shake } from 'radash';

type Obj = Record<string, unknown>;

const cleanObject = (object: object | null, { removeEmpty }: { removeEmpty: boolean } = { removeEmpty: false }) => {
    if (object == null) {
        return null;
    }
    const marked = removeEmpty ? object : markEmptyObjects(object);

    const cleaned = construct(
        shake(crush(marked), (value) => {
            const isValueNullUndefinedOrEmpty = value == null || value === '';
            return isValueNullUndefinedOrEmpty;
        }),
    );

    return removeEmpty ? cleaned : replaceMarkedWithEmpty(cleaned);
};

const markEmptyObjects = (object: object): object => {
    const condition = (object: object) => {
        return Object.keys(object).length === 0;
    };
    const marked = { __empty: true };

    return safeTraversalReplace(object as Obj, condition, marked);
};

const replaceMarkedWithEmpty = (object: object): object => {
    const condition = (object: Obj) => {
        return object.__empty as boolean;
    };
    const emptyObject = {};

    return safeTraversalReplace(object as Obj, condition, emptyObject);
};

const safeTraversalReplace = (object: Obj, condition: (object: Obj) => boolean, conditionResult: object): object => {
    const result: Obj = {};
    Object.keys(object).forEach((key) => {
        result[key] = traverseObjectReplace(object[key] as Obj, condition, conditionResult);
    });
    return result;
};

const traverseObjectReplace = (object: Obj, condition: (object: Obj) => boolean, conditionResult: object): object => {
    if (Array.isArray(object)) {
        return object.map((item) => traverseObjectReplace(item, condition, conditionResult));
    }

    if (typeof object === 'object' && object !== null) {
        if (condition(object)) {
            return conditionResult;
        }
        const result = { ...object };
        for (const key in result) {
            result[key] = traverseObjectReplace(result[key] as Obj, condition, conditionResult);
        }
        return result;
    }

    return object;
};

const removeUndefined = (object: object): object => {
    const objectWithoutUndefinedFields = Object.fromEntries(
        Object.entries(object).filter(([, value]) => value !== undefined),
    );
    return objectWithoutUndefinedFields;
};

const removeEmptyStrings = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map(removeEmptyStrings) as T;
    }

    if (isObject(value)) {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([, nested]) => nested !== '')
                .map(([key, nested]) => [key, removeEmptyStrings(nested)]),
        ) as T;
    }

    return value;
};

export { cleanObject, removeEmptyStrings, removeUndefined };
